import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import React, { useMemo } from 'react'
import { CustomManufacturerFields } from './CustomManufacturerFields'

export interface ExistingCustomManufacturer {
    name: string
    id: string
}

interface CustomManufacturerFieldsWrapperProps {
    selectedProjectElementNode?: Partial<ProjectElementNode>
    isHidden?: boolean
    /** Family-level only: baseElementId of the owning product type. Used to
     * look up the project-specific elementId via familyAddedProductTypes. */
    parentElementId?: string
    /** ID of the rootArticle this row lives under. Required to scope refetches
     * to the affected article after a custom-manufacturer save. */
    rootArticleId?: string
}

export const CustomManufacturerFieldsWrapper: React.FC<CustomManufacturerFieldsWrapperProps> = ({
    selectedProjectElementNode,
    isHidden = false,
    parentElementId,
    rootArticleId,
}) => {
    const elementId = selectedProjectElementNode?.elementId ?? ''
    const projectId = selectedProjectElementNode?.projectId ?? ''

    const { parentFamilyId } = useElementItemShallowSelector((s) => ({
        parentFamilyId: s.elementItemValue.parentFamilyId,
    }))

    const { familyAddedProductTypes } = useShallowSelector((state) => ({
        familyAddedProductTypes: state.familyAddedProductTypes as ProjectElementNode[],
    }))
    const parentProductTypeElementId = useMemo(
        () =>
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
                ? elementId
                : familyAddedProductTypes?.find(
                      (item) => item?.baseElementId === parentElementId
                  )?.elementId ?? '',
        [
            selectedProjectElementNode?.elementType,
            elementId,
            familyAddedProductTypes,
            parentElementId,
        ]
    )

    // For PRODUCTTYPE: use parentFamilyId (the family's elementId)
    // For PRODUCTFAMILY: use elementId (the family itself)
    const productFamilyId =
        selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
            ? parentFamilyId ?? ''
            : selectedProjectElementNode?.elementId ?? ''

    if (
        !elementId ||
        !productFamilyId ||
        !projectId ||
        !parentProductTypeElementId
    ) {
        return null
    }

    return (
        <CustomManufacturerFields
            elementId={parentProductTypeElementId}
            productFamilyId={productFamilyId}
            projectId={projectId}
            parentElementId={parentElementId}
            isHidden={isHidden}
            rootArticleId={rootArticleId}
        />
    )
}
